import React, { ComponentProps, ReactElement, Ref, useRef } from 'react';

import PropTypes from 'prop-types';

import {
  stylePropType,
  StylableComponent,
  StyleProp,
} from '@adsk/alloy-react-theme';

import Token from './Token';
import { defaultRenderToken } from '../../helpers/defaultRenderers';
import { GenericItem, GetSelectedItemProps, ItemToString } from '../../types';

export type TokensProps<TItem extends GenericItem> =
  StylableComponent<HTMLDivElement> & {
    selectedItems: TItem[];
    getSelectedItemProps: GetSelectedItemProps<TItem>;
    removeSelectedItem: (item: TItem) => void;
    itemToString: ItemToString<TItem>;
    renderToken?: (args: ComponentProps<typeof Token>) => React.ReactNode;
    renderLeftComponent?: (item: TItem) => React.ReactNode;
    disabled?: boolean;
  };

const TokensWrapped = <TItem extends GenericItem>(
  {
    className,
    style,
    selectedItems,
    getSelectedItemProps,
    removeSelectedItem,
    itemToString,
    renderToken = defaultRenderToken,
    renderLeftComponent: renderEachLeftComponent,
    disabled,
    ...props
  }: TokensProps<TItem>,
  ref: Ref<HTMLDivElement>,
) => {
  const tokenRefs = useRef<Map<number, HTMLDivElement>>(new Map());

  const handleRemoveWithFocus = (selectedItem: TItem, index: number) => {
    removeSelectedItem(selectedItem);

    // Focus management after deletion
    requestAnimationFrame(() => {
      const nextIndex = index < selectedItems.length - 1 ? index : index - 1;
      const nextToken = tokenRefs.current.get(nextIndex);

      if (nextToken) {
        nextToken.focus();
      }
    });
  };

  return (
    <div
      ref={ref}
      className={className}
      css={[
        {
          display: 'flex',
          flexWrap: 'wrap',
          flex: 1,
          overflowY: 'auto',
          maxHeight: 'inherit',
        },
        style,
      ]}
      {...props}
    >
      {selectedItems.map((selectedItem, index) => {
        const {
          style: tokenStyle,
          css: _ignored,
          ...selectedItemProps
        } = getSelectedItemProps({
          selectedItem,
          index,
        });

        return renderToken({
          key: `selected-item-${index}`,
          ref: (el: HTMLDivElement | null) => {
            if (el) {
              tokenRefs.current.set(index, el);
            } else {
              tokenRefs.current.delete(index);
            }
          },
          style: tokenStyle as StyleProp,
          onClickX: () => removeSelectedItem(selectedItem),
          onRemove: () => handleRemoveWithFocus(selectedItem, index),
          disabled,
          ...selectedItemProps,
          content: itemToString(selectedItem),
          ...(renderEachLeftComponent && {
            renderLeftComponent: () => renderEachLeftComponent(selectedItem),
          }),
        });
      })}
    </div>
  );
};

const Tokens = React.forwardRef(TokensWrapped);

Tokens.displayName = 'Tokens';

Tokens.propTypes = {
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** Array of selected items to represent with Tokens */
  selectedItems: PropTypes.array.isRequired,
  /** Function that returns props which will be applied to each Token */
  getSelectedItemProps: PropTypes.func.isRequired,
  /** Function executed upon clicking the X icon of a Token */
  removeSelectedItem: PropTypes.func.isRequired,
  /** Function which converts an item to its string representation */
  itemToString: PropTypes.func.isRequired,
  /**
   * Function to render the token. You can provide your own custom token, provide the
   * default token with some customization, or swap it out entirely for any of the compatible element
   */
  renderToken: PropTypes.func,
  /** component to render to the left of each token's content (outside the overflow tooltip) */
  renderLeftComponent: PropTypes.func,
};

export default Tokens as <T extends GenericItem>(
  p: TokensProps<T> & { ref?: Ref<HTMLDivElement> },
) => ReactElement;
